/**
 * Yarnly Crochet — theme.js
 *
 * Foundation runtime for the theme: pubsub, shared utilities, the base custom
 * elements (drawer, modal, quantity, carousel, product form, localization form,
 * lazy product recommendations), the landing engine pieces that also render
 * off the product page (landing CTAs, scroll CTAs, poster-first video embeds),
 * the --viewport-width updater and the reveal-on-scroll observer.
 *
 * Pubsub topics: cart:update, cart:error, drawer:open/close, modal:open/close,
 * variant:change { sectionId, variantId, available, mediaId, productId }
 * (published by product.js) and video:play { source, preview }.
 *
 * Plain ES2020, no modules, no build step. `window.Yarnly` is pre-created by
 * an inline script in layout/theme.liquid with `routes`, `settings` and
 * `strings`; this file only extends it. The file is safe to evaluate more than
 * once in the same window: every registration is guarded and one-time state
 * lives on `window.Yarnly`.
 */
(function () {
  'use strict';

  const S = (window.Yarnly = window.Yarnly || {});
  S.routes = S.routes || {};
  S.settings = S.settings || {};
  S.strings = S.strings || {};

  /* ------------------------------------------------------------------------ */
  /* pubsub                                                                    */
  /* ------------------------------------------------------------------------ */

  // Tiny event bus. Kept across double evaluation so subscriptions survive.
  if (!S.pubsub) {
    const channels = {};
    S.pubsub = {
      subscribe(name, callback) {
        if (typeof callback !== 'function') return function () {};
        (channels[name] = channels[name] || []).push(callback);
        return function unsubscribe() {
          channels[name] = (channels[name] || []).filter((fn) => fn !== callback);
        };
      },
      publish(name, payload) {
        (channels[name] || []).slice().forEach((fn) => {
          try {
            fn(payload);
          } catch (error) {
            // Surface the error without breaking the other subscribers.
            setTimeout(() => {
              throw error;
            });
          }
        });
      }
    };
  }

  /* ------------------------------------------------------------------------ */
  /* utils                                                                     */
  /* ------------------------------------------------------------------------ */

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex^="-"])'
  ].join(',');

  function debounce(fn, wait) {
    let timer = null;
    return function debounced() {
      const args = arguments;
      const context = this;
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(context, args), wait);
    };
  }

  function fetchConfig(type) {
    const accept = type === 'javascript' ? 'text/javascript' : 'application/json';
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: accept }
    };
  }

  // Layout information is unavailable in some environments (tests, printing);
  // in that case treat every element as rendered instead of hiding all of them.
  function hasLayout() {
    return document.documentElement.getClientRects().length > 0;
  }

  function isRendered(element) {
    if (element.closest('[hidden], [inert]')) return false;
    if (!hasLayout()) return true;
    return element.getClientRects().length > 0;
  }

  function getFocusable(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isRendered);
  }

  function ensureFocusable(element) {
    if (!element.matches(FOCUSABLE_SELECTOR) && !element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '-1');
    }
  }

  // Focus trap. Handlers live on S._focusTrap so removeTrapFocus() can always
  // find them, even when called from a different evaluation of this file.
  function trapFocus(container, elementToFocus) {
    if (!container) return;
    removeTrapFocus();
    const target = elementToFocus || container;
    const state = { container };

    state.onFocusIn = (event) => {
      if (container.contains(event.target)) return;
      const items = getFocusable(container);
      const fallback = items[0] || container;
      ensureFocusable(fallback);
      fallback.focus({ preventScroll: true });
    };

    state.onKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      const items = getFocusable(container);
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const outside = !container.contains(active);
      if (event.shiftKey && (active === first || active === container || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('focusin', state.onFocusIn);
    document.addEventListener('keydown', state.onKeyDown);
    S._focusTrap = state;

    ensureFocusable(target);
    target.focus({ preventScroll: true });
  }

  function removeTrapFocus(elementToFocus) {
    const state = S._focusTrap;
    if (state) {
      document.removeEventListener('focusin', state.onFocusIn);
      document.removeEventListener('keydown', state.onKeyDown);
      S._focusTrap = null;
    }
    if (elementToFocus && typeof elementToFocus.focus === 'function' && elementToFocus.isConnected) {
      elementToFocus.focus({ preventScroll: true });
    }
  }

  // Escape inside an open <details>: close it and return focus to its summary.
  function onKeyUpEscape(event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    const openDetails = event.target.closest('details[open]');
    if (!openDetails) return;
    const summary = openDetails.querySelector('summary');
    openDetails.removeAttribute('open');
    if (summary) {
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    }
  }

  function parseHTML(text) {
    return new DOMParser().parseFromString(text, 'text/html');
  }

  // Section ids to request from the cart endpoints (Shopify accepts at most 5).
  function getCartSectionIds() {
    const ids = [];
    document.querySelectorAll('[data-cart-render]').forEach((element) => {
      const id = element.getAttribute('data-cart-render');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (ids.indexOf('cart-live-region') === -1) ids.push('cart-live-region');
    return ids.slice(0, 5);
  }

  function sectionsUrl() {
    const cartPath = S.routes.cart;
    const path = window.location.pathname;
    if (cartPath && path.slice(-cartPath.length) === cartPath) return cartPath;
    return path;
  }

  function cssEscape(value) {
    return window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(value) : value;
  }

  function closestFrom(node, selector) {
    return node && typeof node.closest === 'function' ? node.closest(selector) : null;
  }

  // Replace the contents of every [data-cart-render] target with the freshly
  // rendered section HTML returned by a cart endpoint.
  function renderCartSections(sections) {
    if (!sections || typeof sections !== 'object') return;
    document.querySelectorAll('[data-cart-render]').forEach((element) => {
      const id = element.getAttribute('data-cart-render');
      const html = sections[id];
      if (typeof html !== 'string') return;
      const doc = parseHTML(html);
      const selector = element.getAttribute('data-cart-render-selector');
      let source;
      let target;
      if (selector) {
        source =
          doc.querySelector('#shopify-section-' + cssEscape(id) + ' ' + selector) ||
          doc.querySelector(selector);
        target = element.querySelector(selector) || element;
      } else {
        source = doc.getElementById('shopify-section-' + id) || doc.body;
        target = element;
      }
      if (!source) return;
      target.innerHTML = source.innerHTML;
    });
    document.dispatchEvent(new CustomEvent('cart:rendered', { detail: { sections } }));
  }

  // Write to the polite live region. Cleared first so identical messages
  // are announced again.
  function announce(text) {
    const region =
      document.getElementById('cart-live-region-text') || document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    clearTimeout(S._announceTimer);
    S._announceTimer = setTimeout(() => {
      region.textContent = text || '';
    }, 50);
  }

  // "You can only add {{ quantity }}…" → replaces every {{ key }} found in vars.
  // Also accepts the [key] form that head.liquid uses for pre-translated strings.
  function formatTemplate(str, vars) {
    const values = vars || {};
    return String(str == null ? '' : str).replace(/\{\{\s*(\w+)\s*\}\}|\[(\w+)\]/g, (match, curly, square) => {
      const key = curly || square;
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }

  function isReducedMotion() {
    return (
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function updateURLParam(name, value) {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    const url = new URL(window.location.href);
    if (value === null || value === undefined || value === '') {
      url.searchParams.delete(name);
    } else {
      url.searchParams.set(name, value);
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }

  function addMediaQueryListener(mediaQuery, handler) {
    if (typeof mediaQuery.addEventListener === 'function') mediaQuery.addEventListener('change', handler);
    else if (typeof mediaQuery.addListener === 'function') mediaQuery.addListener(handler);
  }

  function removeMediaQueryListener(mediaQuery, handler) {
    if (typeof mediaQuery.removeEventListener === 'function') mediaQuery.removeEventListener('change', handler);
    else if (typeof mediaQuery.removeListener === 'function') mediaQuery.removeListener(handler);
  }

  function matchesMedia(query) {
    return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  }

  // --viewport-width is the layout width without the scrollbar (100vw includes
  // it on desktop), used by --outer-gutter. head.liquid sets it synchronously
  // before first paint; this keeps it true after a resize. A zero width (no
  // layout yet, print) is ignored so the CSS fallback stays in charge.
  function setViewportWidth() {
    const root = document.documentElement;
    const width = root ? root.clientWidth : 0;
    if (width > 0) root.style.setProperty('--viewport-width', width + 'px');
  }

  // The label a buy button should carry for a variant state: its own
  // data-label while available, otherwise the translated sold out /
  // unavailable string. Shared by the landing CTAs and the sticky bar.
  function variantButtonLabel(button, variantId, available) {
    if (!variantId) return S.strings.unavailable || S.strings.soldOut || '';
    if (!available) return S.strings.soldOut || '';
    return (button && button.getAttribute('data-label')) || S.strings.addToCart || '';
  }

  // Writes a label into a button without touching its icon: into the given
  // label node, else into the button itself when it holds text only.
  function setButtonLabel(button, labelNode, text) {
    if (labelNode) {
      labelNode.textContent = text;
    } else if (button && !button.firstElementChild) {
      button.textContent = text;
    }
  }

  // buy-buttons renders <template data-price-source> inside #buy-buttons-<id>
  // with the selected variant's price; the variant picker swaps that wrapper
  // before it publishes variant:change, so the template is always current.
  function priceSourceFor(sectionId) {
    if (!sectionId) return null;
    const wrapper = document.getElementById('buy-buttons-' + sectionId);
    return wrapper ? wrapper.querySelector('template[data-price-source]') : null;
  }

  S.utils = Object.assign(S.utils || {}, {
    debounce,
    fetchConfig,
    getFocusable,
    trapFocus,
    removeTrapFocus,
    onKeyUpEscape,
    parseHTML,
    getCartSectionIds,
    sectionsUrl,
    renderCartSections,
    announce,
    formatTemplate,
    isReducedMotion,
    updateURLParam,
    matchesMedia,
    setViewportWidth,
    variantButtonLabel,
    setButtonLabel,
    priceSourceFor
  });

  if (!S._viewportWidthBound) {
    S._viewportWidthBound = true;
    // Called through S.utils so a later evaluation of this file stays in charge.
    window.addEventListener(
      'resize',
      debounce(() => S.utils.setViewportWidth(), 100),
      { passive: true }
    );
    setViewportWidth();
  }

  // `has-drawer-open` is the scroll-lock class styled by base.css; it is kept
  // on <body> while any locking drawer or modal is open.
  function syncScrollLock() {
    const drawerOpen = Array.from(document.querySelectorAll('yarnly-drawer.is-open')).some(
      (drawer) => drawer.getAttribute('data-lock-scroll') !== 'false'
    );
    const modalOpen = !!document.querySelector('yarnly-modal.is-open');
    document.body.classList.toggle('has-drawer-open', drawerOpen || modalOpen);
    document.body.classList.toggle('has-modal-open', modalOpen);
  }

  function currentFocus() {
    const active = document.activeElement;
    return active && active !== document.body ? active : null;
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-drawer>                                                         */
  /* ------------------------------------------------------------------------ */

  // Off-canvas panel. Markup: `.drawer__overlay[data-drawer-overlay]` plus a
  // `.drawer__panel[role="dialog"][aria-modal="true"]`. Triggers anywhere in
  // the document use `[data-drawer-open="<id>"]`; close buttons inside use
  // `[data-drawer-close]`. Only one drawer is open at a time.
  if (!customElements.get('yarnly-drawer')) {
    class YarnlyDrawer extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.isOpen = false;
        this.trigger = null;
      }

      connectedCallback() {
        this.panel = this.querySelector('.drawer__panel') || this;
        // Delegated so close buttons and the overlay survive a cart re-render
        // inside the panel (nothing inside is cached).
        this.addEventListener('click', this.onClick);
        this.addEventListener('keyup', this.onKeyUp);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keyup', this.onKeyUp);
        if (this.isOpen) {
          this.isOpen = false;
          this.classList.remove('is-open');
          removeTrapFocus();
          syncScrollLock();
        }
      }

      onClick(event) {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
          return;
        }
        const overlay = event.target.closest('[data-drawer-overlay]');
        if (overlay && overlay.closest('yarnly-drawer') === this) this.close();
      }

      onKeyUp(event) {
        if (event.key === 'Escape' || event.code === 'Escape') this.close();
      }

      open(trigger) {
        if (this.isOpen) return;
        document.querySelectorAll('yarnly-drawer.is-open').forEach((other) => {
          if (other !== this && typeof other.close === 'function') other.close({ restoreFocus: false });
        });

        this.trigger = trigger || currentFocus();
        this.isOpen = true;
        this.panel = this.querySelector('.drawer__panel') || this;
        this.classList.add('is-open');
        if (this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'false');
        if (this.trigger && this.trigger.hasAttribute && this.trigger.hasAttribute('aria-expanded')) {
          this.trigger.setAttribute('aria-expanded', 'true');
        }
        syncScrollLock();

        const panel = this.panel;
        const focusTarget =
          panel.querySelector('[data-drawer-focus], [autofocus]') ||
          getFocusable(panel)[0] ||
          panel.querySelector('[data-drawer-close]') ||
          panel;
        trapFocus(panel, focusTarget);
        // If the panel is still transitioning in (visibility), retry next frame.
        if (!panel.contains(document.activeElement) && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => {
            if (this.isOpen && !panel.contains(document.activeElement)) focusTarget.focus({ preventScroll: true });
          });
        }

        S.pubsub.publish('drawer:open', { id: this.id, drawer: this, trigger: this.trigger });
        this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true, detail: { id: this.id, trigger: this.trigger } }));
      }

      close(options) {
        if (!this.isOpen) return;
        const restoreFocus = !(options && options.restoreFocus === false);
        this.isOpen = false;
        this.classList.remove('is-open');
        if (this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'true');
        if (this.trigger && this.trigger.hasAttribute && this.trigger.hasAttribute('aria-expanded')) {
          this.trigger.setAttribute('aria-expanded', 'false');
        }
        syncScrollLock();
        removeTrapFocus(restoreFocus ? this.trigger : null);

        S.pubsub.publish('drawer:close', { id: this.id, drawer: this });
        this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true, detail: { id: this.id } }));
        this.trigger = null;
      }

      toggle(trigger) {
        if (this.isOpen) this.close();
        else this.open(trigger);
      }
    }
    customElements.define('yarnly-drawer', YarnlyDrawer);
  }
  S.Drawer = customElements.get('yarnly-drawer');

  // Global trigger delegation, installed once per window.
  if (!S._drawerDelegation) {
    S._drawerDelegation = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-drawer-open]');
      if (!trigger) return;
      const drawer = document.getElementById(trigger.getAttribute('data-drawer-open') || '');
      if (!drawer || drawer.tagName.toLowerCase() !== 'yarnly-drawer' || typeof drawer.open !== 'function') {
        return; // leave the <a href> fallback alone
      }
      event.preventDefault();
      if (drawer.isOpen) drawer.close();
      else drawer.open(trigger);
    });
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-modal>                                                          */
  /* ------------------------------------------------------------------------ */

  // Wraps a native <dialog class="modal">. Uses showModal() when available and
  // falls back to the `open` attribute plus our own focus trap.
  if (!customElements.get('yarnly-modal')) {
    class YarnlyModal extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onDialogClick = this.onDialogClick.bind(this);
        this.onDialogClose = this.onDialogClose.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.isOpen = false;
        this.fallback = false;
        this.trigger = null;
      }

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.addEventListener('click', this.onClick);
        this.addEventListener('keyup', this.onKeyUp);
        if (this.dialog) {
          this.dialog.addEventListener('click', this.onDialogClick);
          this.dialog.addEventListener('close', this.onDialogClose);
          this.dialog.addEventListener('cancel', this.onCancel);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keyup', this.onKeyUp);
        if (this.dialog) {
          this.dialog.removeEventListener('click', this.onDialogClick);
          this.dialog.removeEventListener('close', this.onDialogClose);
          this.dialog.removeEventListener('cancel', this.onCancel);
        }
        if (this.isOpen) {
          this.isOpen = false;
          this.classList.remove('is-open');
          removeTrapFocus();
          syncScrollLock();
        }
      }

      onClick(event) {
        if (event.target.closest('[data-modal-close]')) {
          event.preventDefault();
          this.close();
        }
      }

      // A click whose target is the <dialog> itself landed on the backdrop.
      onDialogClick(event) {
        if (event.target === this.dialog) this.close();
      }

      onCancel(event) {
        event.preventDefault();
        this.close();
      }

      onKeyUp(event) {
        if (this.fallback && (event.key === 'Escape' || event.code === 'Escape')) this.close();
      }

      onDialogClose() {
        this.finishClose();
      }

      open(trigger) {
        if (!this.dialog || this.isOpen) return;
        this.trigger = trigger || currentFocus();
        this.isOpen = true;
        this.fallback = typeof this.dialog.showModal !== 'function';
        this.classList.add('is-open');
        syncScrollLock();

        const focusTarget =
          this.dialog.querySelector('[data-modal-focus], [autofocus]') ||
          this.dialog.querySelector('[data-modal-close]') ||
          getFocusable(this.dialog)[0] ||
          this.dialog;

        if (!this.fallback) {
          try {
            if (!this.dialog.open) this.dialog.showModal();
          } catch (error) {
            this.fallback = true;
          }
        }
        if (this.fallback) {
          this.dialog.setAttribute('open', '');
          trapFocus(this.dialog, focusTarget);
        } else if (focusTarget !== this.dialog) {
          focusTarget.focus({ preventScroll: true });
        }

        S.pubsub.publish('modal:open', { id: this.id, modal: this, trigger: this.trigger });
        this.dispatchEvent(new CustomEvent('modal:open', { bubbles: true, detail: { id: this.id } }));
      }

      close() {
        if (!this.isOpen || !this.dialog) return;
        if (!this.fallback && this.dialog.open && typeof this.dialog.close === 'function') {
          this.dialog.close(); // fires `close` → finishClose()
          return;
        }
        this.dialog.removeAttribute('open');
        this.finishClose();
      }

      finishClose() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove('is-open');
        syncScrollLock();
        this.querySelectorAll('video').forEach((video) => {
          if (typeof video.pause === 'function') video.pause();
        });
        if (this.fallback) {
          removeTrapFocus(this.trigger);
        } else if (this.trigger && this.trigger.isConnected) {
          this.trigger.focus({ preventScroll: true });
        }
        S.pubsub.publish('modal:close', { id: this.id, modal: this });
        this.dispatchEvent(new CustomEvent('modal:close', { bubbles: true, detail: { id: this.id } }));
        this.trigger = null;
      }
    }
    customElements.define('yarnly-modal', YarnlyModal);
  }
  S.Modal = customElements.get('yarnly-modal');

  if (!S._modalDelegation) {
    S._modalDelegation = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-modal-open]');
      if (!trigger) return;
      const modal = document.getElementById(trigger.getAttribute('data-modal-open') || '');
      if (!modal || modal.tagName.toLowerCase() !== 'yarnly-modal' || typeof modal.open !== 'function') return;
      event.preventDefault();
      modal.open(trigger);
    });
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-quantity>                                                       */
  /* ------------------------------------------------------------------------ */

  // Stepper around an <input type="number">. Clamps to min/max, snaps to step
  // and dispatches a bubbling `change` so cart/product forms can react.
  if (!customElements.get('yarnly-quantity')) {
    class YarnlyQuantity extends HTMLElement {
      constructor() {
        super();
        this.onMinus = this.onMinus.bind(this);
        this.onPlus = this.onPlus.bind(this);
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.input = this.querySelector('input[type="number"]');
        this.minusButton = this.querySelector('[data-quantity-minus]');
        this.plusButton = this.querySelector('[data-quantity-plus]');
        if (!this.input) return;
        if (this.minusButton) this.minusButton.addEventListener('click', this.onMinus);
        if (this.plusButton) this.plusButton.addEventListener('click', this.onPlus);
        this.input.addEventListener('change', this.onChange);
        this.update();
      }

      disconnectedCallback() {
        if (this.minusButton) this.minusButton.removeEventListener('click', this.onMinus);
        if (this.plusButton) this.plusButton.removeEventListener('click', this.onPlus);
        if (this.input) this.input.removeEventListener('change', this.onChange);
      }

      get bounds() {
        const input = this.input;
        const min = input.min !== '' && !isNaN(parseFloat(input.min)) ? parseFloat(input.min) : 0;
        const max = input.max !== '' && !isNaN(parseFloat(input.max)) ? parseFloat(input.max) : Infinity;
        const step = parseFloat(input.step) > 0 ? parseFloat(input.step) : 1;
        return { min, max, step };
      }

      get value() {
        const parsed = parseFloat(this.input.value);
        return isNaN(parsed) ? this.bounds.min : parsed;
      }

      clamp(value) {
        const { min, max, step } = this.bounds;
        let next = Math.round((value - min) / step) * step + min;
        next = Math.max(min, Math.min(max, next));
        return Math.round(next * 1000) / 1000;
      }

      onMinus(event) {
        event.preventDefault();
        this.stepBy(-1);
      }

      onPlus(event) {
        event.preventDefault();
        this.stepBy(1);
      }

      stepBy(direction) {
        const current = this.value;
        const next = this.clamp(current + direction * this.bounds.step);
        if (next === current) {
          this.update();
          return;
        }
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.update();
      }

      onChange() {
        const clamped = this.clamp(this.value);
        if (String(clamped) !== this.input.value) this.input.value = clamped;
        this.update();
      }

      update() {
        const value = this.value;
        const { min, max } = this.bounds;
        this.setButtonState(this.minusButton, value <= min);
        this.setButtonState(this.plusButton, value >= max);
      }

      setButtonState(button, disabled) {
        if (!button) return;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        button.classList.toggle('is-disabled', disabled);
      }
    }
    customElements.define('yarnly-quantity', YarnlyQuantity);
  }
  S.Quantity = customElements.get('yarnly-quantity');

  /* ------------------------------------------------------------------------ */
  /* <yarnly-carousel>                                                       */
  /* ------------------------------------------------------------------------ */

  // Progressive carousel on top of a CSS scroll-snap track. Children:
  // [data-carousel-track] > [data-carousel-slide]; optional prev/next/dots/
  // pause controls. Attributes: data-autoplay="<ms>", data-loop,
  // data-mobile-only (enhance below 750px only), data-go-to-label.
  if (!customElements.get('yarnly-carousel')) {
    class YarnlyCarousel extends HTMLElement {
      constructor() {
        super();
        [
          'onPrev',
          'onNext',
          'onScroll',
          'onKeyDown',
          'onDotClick',
          'onPauseToggle',
          'onPointerEnter',
          'onPointerLeave',
          'onFocusIn',
          'onFocusOut',
          'onMediaChange',
          'onBlockSelect',
          'onBlockDeselect',
          'onVisibilityChange'
        ].forEach((name) => {
          this[name] = this[name].bind(this);
        });
        this.onScrollDebounced = debounce(this.onScroll, 80);
        this.current = 0;
        this.enhanced = false;
        this.timer = null;
        this.userPaused = false;
        this.editorPaused = false;
        this.hovering = false;
        this.focused = false;
        this.slides = [];
      }

      // Only elements that belong to this carousel (not a nested one).
      own(selector) {
        return Array.from(this.querySelectorAll(selector)).filter(
          (element) => element.closest('yarnly-carousel') === this
        );
      }

      connectedCallback() {
        this.track = this.own('[data-carousel-track]')[0] || null;
        if (!this.track) return;
        this.slides = this.own('[data-carousel-slide]');
        this.prevButton = this.own('[data-carousel-prev]')[0] || null;
        this.nextButton = this.own('[data-carousel-next]')[0] || null;
        this.dotsContainer = this.own('[data-carousel-dots]')[0] || null;
        this.pauseButton = this.own('[data-carousel-pause]')[0] || null;
        this.statusElement = this.own('[data-carousel-status]')[0] || null;
        this.loop = this.hasAttribute('data-loop');
        this.autoplayMs = parseInt(this.getAttribute('data-autoplay'), 10) || 0;

        if (this.hasAttribute('data-mobile-only')) {
          if (typeof window.matchMedia === 'function') {
            this.mediaQuery = window.matchMedia('(max-width: 749px)');
            addMediaQueryListener(this.mediaQuery, this.onMediaChange);
            this.onMediaChange();
          }
        } else {
          this.enhance();
        }

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }
      }

      disconnectedCallback() {
        this.teardown();
        if (this.mediaQuery) removeMediaQueryListener(this.mediaQuery, this.onMediaChange);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      onMediaChange() {
        if (this.mediaQuery && this.mediaQuery.matches) this.enhance();
        else this.teardown();
      }

      enhance() {
        if (this.enhanced || !this.track) return;
        this.enhanced = true;
        this.setAttribute('data-enhanced', '');
        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
        if (this.pauseButton) this.pauseButton.addEventListener('click', this.onPauseToggle);
        this.track.addEventListener('scroll', this.onScrollDebounced, { passive: true });
        this.track.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        this.buildDots();
        this.updateControls();
        this.startAutoplay();
      }

      teardown() {
        if (!this.enhanced) return;
        this.enhanced = false;
        this.removeAttribute('data-enhanced');
        if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
        if (this.pauseButton) this.pauseButton.removeEventListener('click', this.onPauseToggle);
        this.track.removeEventListener('scroll', this.onScrollDebounced);
        this.track.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stopAutoplay();
        if (this.dotsContainer) {
          this.dotsContainer.removeEventListener('click', this.onDotClick);
          this.dotsContainer.innerHTML = '';
        }
      }

      get count() {
        return this.slides.length;
      }

      goTo(index, options) {
        const opts = options || {};
        const count = this.count;
        if (!count || !this.track) return;
        let target = Number(index) || 0;
        if (target < 0 || target >= count) {
          if (this.loop || opts.wrap) target = ((target % count) + count) % count;
          else target = Math.max(0, Math.min(count - 1, target));
        }
        const slide = this.slides[target];
        if (!slide) return;
        const left =
          slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
        const behavior = opts.instant || isReducedMotion() ? 'auto' : 'smooth';
        if (typeof this.track.scrollTo === 'function') this.track.scrollTo({ left, behavior });
        else this.track.scrollLeft = left;
        this.setCurrent(target);
      }

      setCurrent(index) {
        const changed = index !== this.current;
        this.current = index;
        this.updateControls();
        if (changed) {
          this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index } }));
        }
      }

      onPrev(event) {
        event.preventDefault();
        if (this.current <= 0 && !this.loop) return;
        this.goTo(this.current - 1);
      }

      onNext(event) {
        event.preventDefault();
        if (this.current >= this.count - 1 && !this.loop) return;
        this.goTo(this.current + 1);
      }

      onKeyDown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.current - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.current + 1);
        }
      }

      // Nearest slide to the track's left edge after the user scrolls.
      onScroll() {
        if (!this.enhanced || !this.count) return;
        const track = this.track;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        let best = this.current;
        if (atEnd && track.scrollWidth > track.clientWidth) {
          best = this.count - 1;
        } else {
          const trackLeft = track.getBoundingClientRect().left;
          let bestDistance = Infinity;
          this.slides.forEach((slide, i) => {
            const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
            if (distance < bestDistance) {
              bestDistance = distance;
              best = i;
            }
          });
        }
        if (best !== this.current) this.setCurrent(best);
      }

      updateControls() {
        const atStart = this.current <= 0;
        const atEnd = this.current >= this.count - 1;
        if (this.prevButton) this.prevButton.setAttribute('aria-disabled', String(!this.loop && atStart));
        if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(!this.loop && atEnd));
        if (this.dotsContainer) {
          Array.from(this.dotsContainer.children).forEach((dot, i) => {
            const active = i === this.current;
            dot.classList.toggle('is-active', active);
            if (active) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        }
        if (this.statusElement && this.dataset.slideOfLabel) {
          this.statusElement.textContent = formatTemplate(this.dataset.slideOfLabel, {
            current: this.current + 1,
            total: this.count
          });
        }
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.current));
        this.setAttribute('data-current', String(this.current));
      }

      buildDots() {
        const container = this.dotsContainer;
        if (!container) return;
        const template = this.dataset.goToLabel || S.strings.goToSlide || 'Go to slide {{ index }}';
        container.innerHTML = '';
        this.slides.forEach((slide, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'carousel__dot';
          dot.setAttribute('aria-label', formatTemplate(template, { index: i + 1 }));
          dot.setAttribute('data-index', String(i));
          container.appendChild(dot);
        });
        container.addEventListener('click', this.onDotClick);
      }

      onDotClick(event) {
        const dot = event.target.closest('[data-index]');
        if (!dot || !this.dotsContainer.contains(dot)) return;
        event.preventDefault();
        this.goTo(parseInt(dot.getAttribute('data-index'), 10));
      }

      get autoplayAllowed() {
        return (
          this.enhanced &&
          this.autoplayMs > 0 &&
          this.count > 1 &&
          !this.userPaused &&
          !this.editorPaused &&
          !this.hovering &&
          !this.focused &&
          !document.hidden &&
          !isReducedMotion()
        );
      }

      startAutoplay() {
        this.stopAutoplay();
        if (!this.autoplayAllowed) return;
        this.timer = setInterval(() => this.goTo(this.current + 1, { wrap: true }), this.autoplayMs);
        this.classList.add('is-autoplaying');
      }

      stopAutoplay() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
        this.classList.remove('is-autoplaying');
      }

      onPauseToggle(event) {
        event.preventDefault();
        this.userPaused = !this.userPaused;
        const button = this.pauseButton;
        button.setAttribute('aria-pressed', String(this.userPaused));
        const label = this.userPaused ? button.dataset.labelPlay : button.dataset.labelPause;
        if (label) button.setAttribute('aria-label', label);
        this.classList.toggle('is-paused', this.userPaused);
        if (this.userPaused) this.stopAutoplay();
        else this.startAutoplay();
      }

      onPointerEnter() {
        this.hovering = true;
        this.stopAutoplay();
      }

      onPointerLeave() {
        this.hovering = false;
        this.startAutoplay();
      }

      onFocusIn() {
        this.focused = true;
        this.stopAutoplay();
      }

      onFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.startAutoplay();
      }

      onVisibilityChange() {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      }

      slideIndexForBlock(blockId) {
        return this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      }

      onBlockSelect(event) {
        const index = this.slideIndexForBlock(event.detail && event.detail.blockId);
        if (index === -1) return;
        this.editorPaused = true;
        this.stopAutoplay();
        this.goTo(index, { instant: true });
      }

      onBlockDeselect(event) {
        if (this.slideIndexForBlock(event.detail && event.detail.blockId) === -1) return;
        this.editorPaused = false;
        this.startAutoplay();
      }
    }
    customElements.define('yarnly-carousel', YarnlyCarousel);
  }
  S.Carousel = customElements.get('yarnly-carousel');

  /* ------------------------------------------------------------------------ */
  /* <yarnly-product-form>                                                   */
  /* ------------------------------------------------------------------------ */

  // Fetch-submits the wrapped product <form> to the cart add endpoint, then
  // re-renders the cart sections, announces, and opens the cart drawer (or
  // shows an inline status). Add `data-native` to keep the browser submit.
  //
  // data-after-add="cart" (default) follows S.settings.cartType as above;
  // data-after-add="checkout" submits the sibling
  // <form data-checkout-form action="{{ routes.cart_url }}" method="post">
  // (it posts `checkout`, which Shopify redirects to checkout) once the item is
  // in the cart. That form sits next to the product form — inside this element
  // or as its sibling — never inside it.
  if (!customElements.get('yarnly-product-form')) {
    class YarnlyProductForm extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.submitting = false;
        this.redirecting = false;
      }

      connectedCallback() {
        // Listen on the element so an inner form swap keeps working.
        this.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.onSubmit);
        window.removeEventListener('pageshow', this.onPageShow);
      }

      get form() {
        return this.querySelector('form:not([data-checkout-form])');
      }

      get checkoutForm() {
        const own = this.querySelector('form[data-checkout-form]');
        if (own) return own;
        const parent = this.parentElement;
        if (!parent) return null;
        return Array.from(parent.children).find((child) => child.matches('form[data-checkout-form]')) || null;
      }

      get submitButton() {
        const form = this.form;
        return form ? form.querySelector('[type="submit"]') : null;
      }

      get statusElement() {
        return this.querySelector('[data-form-status]');
      }

      onSubmit(event) {
        const form = event.target.closest ? event.target.closest('form') : null;
        if (!form || this.hasAttribute('data-native') || !S.routes.cartAdd || typeof window.fetch !== 'function') {
          return; // native submit
        }
        if (form.hasAttribute('data-checkout-form')) return; // the checkout hand-off posts natively
        if (Array.from(form.querySelectorAll('input[type="file"]')).some((input) => input.files && input.files.length)) {
          return; // files cannot travel as JSON; let the browser post the form
        }
        event.preventDefault();
        if (this.submitting) return;

        this.submitting = true;
        this.setLoading(true);
        this.clearStatus();

        const body = this.buildBody(form);
        const config = fetchConfig('javascript');
        config.body = JSON.stringify(body);

        fetch(S.routes.cartAdd + '.js', config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message || S.strings.cartError);
              S.pubsub.publish('cart:error', { source: 'product-form', response, form });
              return;
            }
            renderCartSections(response.sections);
            announce(S.strings.addedToCart);
            S.pubsub.publish('cart:update', { source: 'product-form', response, sections: response.sections });
            this.onAdded();
          })
          .catch(() => {
            this.showError(S.strings.cartError);
            S.pubsub.publish('cart:error', { source: 'product-form', response: null, form });
          })
          .finally(() => {
            this.submitting = false;
            // On the way to checkout the button keeps its spinner until the
            // page unloads (or comes back from the back/forward cache).
            if (!this.redirecting) this.setLoading(false);
          });
      }

      onPageShow() {
        this.redirecting = false;
        this.setLoading(false);
      }

      buildBody(form) {
        const data = new FormData(form);
        const id = data.get('id');
        const quantity = parseInt(data.get('quantity'), 10);
        const item = {
          id: /^\d+$/.test(String(id)) ? Number(id) : id,
          quantity: quantity > 0 ? quantity : 1
        };
        const properties = {};
        let hasProperties = false;
        data.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match || value === '' || typeof value !== 'string') return;
          properties[match[1]] = value;
          hasProperties = true;
        });
        if (hasProperties) item.properties = properties;
        const sellingPlan = data.get('selling_plan');
        if (sellingPlan) item.selling_plan = /^\d+$/.test(String(sellingPlan)) ? Number(sellingPlan) : sellingPlan;
        return { items: [item], sections: getCartSectionIds(), sections_url: sectionsUrl() };
      }

      onAdded() {
        if (this.getAttribute('data-after-add') === 'checkout') {
          const checkoutForm = this.checkoutForm;
          if (checkoutForm) {
            this.redirecting = true;
            window.addEventListener('pageshow', this.onPageShow, { once: true });
            // submit() skips the submit event, so no listener can turn the
            // hand-off into another cart add.
            checkoutForm.submit();
            return;
          }
        }
        const drawer = document.getElementById('cart-drawer');
        const status = this.statusElement;
        if (S.settings.cartType === 'drawer' && drawer && typeof drawer.open === 'function') {
          drawer.open(this.submitButton || this);
        } else if (status) {
          this.showSuccess();
        } else if (S.routes.cart) {
          window.location.href = S.routes.cart;
        }
      }

      setLoading(on) {
        const button = this.submitButton;
        if (!button) return;
        button.classList.toggle('btn--loading', on);
        if (on) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      }

      clearStatus() {
        const status = this.statusElement;
        if (!status) return;
        status.textContent = '';
        status.classList.remove('form-status--error', 'form-status--success');
        status.setAttribute('role', 'status');
      }

      showError(text) {
        const status = this.statusElement;
        if (!status) return;
        status.classList.add('form-status');
        status.classList.remove('form-status--success');
        status.classList.add('form-status--error');
        status.setAttribute('role', 'alert');
        status.textContent = text || '';
      }

      showSuccess() {
        const status = this.statusElement;
        if (!status) return;
        status.classList.add('form-status');
        status.classList.remove('form-status--error');
        status.classList.add('form-status--success');
        status.setAttribute('role', 'status');
        status.textContent = '';
        const message = document.createElement('span');
        message.textContent = S.strings.addedToCart || '';
        status.appendChild(message);
        if (S.routes.cart) {
          status.appendChild(document.createTextNode(' '));
          const link = document.createElement('a');
          link.className = 'link';
          link.href = S.routes.cart;
          link.textContent = S.strings.viewCart || S.routes.cart;
          status.appendChild(link);
        }
      }
    }
    customElements.define('yarnly-product-form', YarnlyProductForm);
  }
  S.ProductForm = customElements.get('yarnly-product-form');

  /* ------------------------------------------------------------------------ */
  /* <yarnly-landing-cta>                                                     */
  /* ------------------------------------------------------------------------ */

  // A repeated call to action (snippets/landing-cta.liquid) outside the hero:
  // mid-page, the final band, the homepage. It lives here rather than in
  // product.js because CTAs also render on pages without the product runtime.
  //
  //   <yarnly-landing-cta data-product-id="123">
  //     <yarnly-product-form data-after-add="cart|checkout">
  //       <form …><input type="hidden" name="id" data-landing-cta-variant>
  //         <button type="submit" data-label="Get the Panda Pattern">
  //           <span data-landing-cta-label>…</span> (icon)</button>
  //         <div data-form-status role="status"></div></form>
  //       <form data-checkout-form hidden>…</form>   (checkout action only)
  //     </yarnly-product-form>
  //     <… data-landing-cta-price>…</…>                (optional)
  //   </yarnly-landing-cta>
  //
  // It follows the variant chosen in the hero picker: every `variant:change`
  // whose productId matches data-product-id rewrites the hidden id, the
  // button's disabled state and label, and the optional price line (copied
  // from the publishing section's price source). Scroll and link CTAs carry
  // no form, so only their price line follows.
  if (!customElements.get('yarnly-landing-cta')) {
    class YarnlyLandingCta extends HTMLElement {
      constructor() {
        super();
        this.onVariantChange = this.onVariantChange.bind(this);
      }

      connectedCallback() {
        this.unsubscribe = S.pubsub.subscribe('variant:change', this.onVariantChange);
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        this.unsubscribe = null;
      }

      onVariantChange(payload) {
        const productId = this.getAttribute('data-product-id');
        if (!payload || !productId || String(payload.productId) !== String(productId)) return;
        const variantId = payload.variantId ? String(payload.variantId) : '';
        const available = !!variantId && payload.available === true;

        const input = this.querySelector('input[name="id"][data-landing-cta-variant]');
        if (input) {
          input.value = variantId;
          input.disabled = !variantId;
        }
        const button = this.querySelector('[type="submit"]');
        if (button) {
          button.disabled = !available;
          setButtonLabel(
            button,
            button.querySelector('[data-landing-cta-label]'),
            variantButtonLabel(button, variantId, available)
          );
        }

        const price = this.querySelector('[data-landing-cta-price]');
        const source = priceSourceFor(payload.sectionId);
        if (price && source) price.innerHTML = source.innerHTML;
      }
    }
    customElements.define('yarnly-landing-cta', YarnlyLandingCta);
  }
  S.LandingCta = customElements.get('yarnly-landing-cta');

  /* ------------------------------------------------------------------------ */
  /* Scroll CTAs                                                              */
  /* ------------------------------------------------------------------------ */

  // `<a href="#anchor" data-landing-scroll>`: a CTA whose action is "scroll".
  // An explicit anchor on this page wins; `#MainContent` (the default) means
  // "the buy box", i.e. the first [data-buy-anchor]. The target is scrolled to
  // (smoothly unless reduced motion is asked for) and focused, so keyboard and
  // screen-reader users land where sighted users do. Without JS the link is a
  // plain in-page jump.
  function landingScrollTarget(link) {
    if (link.pathname && link.pathname !== window.location.pathname) return null;
    let hash = '';
    try {
      hash = decodeURIComponent((link.hash || '').slice(1));
    } catch (error) {
      hash = (link.hash || '').slice(1);
    }
    let target = hash && hash !== 'MainContent' ? document.getElementById(hash) : null;
    if (!target) target = document.querySelector('[data-buy-anchor]');
    if (!target && hash) target = document.getElementById(hash);
    return target;
  }

  function scrollToLandingTarget(target) {
    target.scrollIntoView({ behavior: isReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    ensureFocusable(target);
    target.focus({ preventScroll: true });
  }

  if (!S._landingScrollBound) {
    S._landingScrollBound = true;
    document.addEventListener('click', (event) => {
      const link = closestFrom(event.target, 'a[data-landing-scroll]');
      if (!link || event.defaultPrevented) return;
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = landingScrollTarget(link);
      if (!target) return;
      event.preventDefault();
      scrollToLandingTarget(target);
    });
  }

  /* ------------------------------------------------------------------------ */
  /* <yarnly-video-embed>                                                     */
  /* ------------------------------------------------------------------------ */

  // Poster-first video facade (snippets/video-embed.liquid). Nothing
  // third-party is in the DOM until the visitor asks for it:
  //
  //   <yarnly-video-embed data-autoplay-hover="true|false" style="--ratio: 9/16">
  //     <button type="button" data-video-play aria-label="…">poster + play</button>
  //     <template data-media><video …> | <iframe data-provider="youtube|vimeo|tiktok|instagram" …></template>
  //     <div data-media-target></div>
  //   </yarnly-video-embed>
  //
  // - Click on [data-video-play]: the template is cloned into
  //   [data-media-target], the poster is hidden, the player is focused and
  //   started (iframes autoplay from their src; the click is the gesture).
  // - One player at a time: every start publishes `video:play`
  //   { source, preview } and the others pause. A muted hover preview only
  //   pauses other previews, never a video the visitor started.
  // - Pauses when less than a quarter of it is visible.
  // - Hover preview (data-autoplay-hover="true"): <video> sources only, on a
  //   fine pointer that can hover, never with reduced motion; muted, paused on
  //   leave, and dropped once the visitor interacts with the embed.
  // - YouTube and Vimeo pause through postMessage; TikTok and Instagram have
  //   no pause API, so they unload back to the poster.
  if (!customElements.get('yarnly-video-embed')) {
    class YarnlyVideoEmbed extends HTMLElement {
      constructor() {
        super();
        ['onClick', 'onPointerEnter', 'onPointerLeave', 'onOtherPlay', 'onIntersect', 'onMediaPlay', 'onMediaPause'].forEach(
          (name) => {
            this[name] = this[name].bind(this);
          }
        );
        this.player = null;
        this.previewing = false;
        this.engaged = false;
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.unsubscribe = S.pubsub.subscribe('video:play', this.onOtherPlay);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        if (this.unsubscribe) this.unsubscribe();
        this.unsubscribe = null;
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        this.pause();
      }

      get template() {
        return this.querySelector('template[data-media]');
      }

      get mediaTarget() {
        return this.querySelector('[data-media-target]');
      }

      get poster() {
        return this.querySelector('[data-video-play]');
      }

      get isVideo() {
        return !!this.player && this.player.tagName === 'VIDEO';
      }

      onClick(event) {
        // Any click inside the embed is an explicit choice: stop treating it
        // as a hover preview (so leaving no longer pauses it).
        this.engaged = true;
        this.previewing = false;
        const button = closestFrom(event.target, '[data-video-play]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        this.load({ focus: true });
      }

      canPreview() {
        if (this.getAttribute('data-autoplay-hover') !== 'true' || this.engaged) return false;
        if (isReducedMotion() || !matchesMedia('(hover: hover) and (pointer: fine)')) return false;
        if (this.player) return this.isVideo;
        const template = this.template;
        return !!(template && template.content && template.content.querySelector('video'));
      }

      onPointerEnter(event) {
        if (event && event.pointerType && event.pointerType !== 'mouse') return;
        if (!this.canPreview()) return;
        this.previewing = true;
        this.load({ focus: false, muted: true });
      }

      onPointerLeave() {
        if (!this.previewing) return;
        this.pause();
      }

      load(options) {
        const opts = options || {};
        if (this.player) {
          if (opts.muted && this.isVideo) this.player.muted = true;
          this.play();
          return;
        }
        const template = this.template;
        const target = this.mediaTarget;
        if (!template || !target) return;
        const fragment = template.content.cloneNode(true);
        const player = fragment.querySelector('video, iframe');
        if (!player) return;
        target.appendChild(fragment);
        this.player = player;
        this.classList.add('is-loaded');
        this.setAttribute('data-loaded', 'true');
        const poster = this.poster;
        if (poster) poster.hidden = true;

        if (player.tagName === 'VIDEO') {
          player.setAttribute('playsinline', '');
          if (opts.muted) player.muted = true;
          player.addEventListener('play', this.onMediaPlay);
          player.addEventListener('pause', this.onMediaPause);
          player.addEventListener('ended', this.onMediaPause);
        }
        this.observe();
        if (opts.focus && typeof player.focus === 'function') {
          if (!player.hasAttribute('tabindex')) player.setAttribute('tabindex', '-1');
          player.focus({ preventScroll: true });
        }
        if (player.tagName === 'VIDEO') this.play();
        else this.onMediaPlay(); // the iframe starts itself from its src
      }

      play() {
        const player = this.player;
        if (!player) return;
        if (player.tagName === 'VIDEO') {
          const attempt = typeof player.play === 'function' ? player.play() : null;
          if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
        } else {
          this.postToFrame('play');
          this.onMediaPlay();
        }
      }

      pause() {
        const player = this.player;
        if (!player) return;
        if (player.tagName === 'VIDEO') {
          if (!player.paused && typeof player.pause === 'function') player.pause();
          return;
        }
        const provider = player.getAttribute('data-provider');
        if (provider === 'youtube' || provider === 'vimeo') {
          this.postToFrame('pause');
          this.onMediaPause();
        } else {
          this.reset();
        }
      }

      // Back to the poster: used for providers that cannot be paused.
      reset() {
        const hadFocus = this.contains(document.activeElement);
        if (this.observer) this.observer.disconnect();
        this.observer = null;
        const target = this.mediaTarget;
        if (target) target.innerHTML = '';
        this.player = null;
        this.previewing = false;
        this.classList.remove('is-loaded', 'is-playing');
        this.removeAttribute('data-loaded');
        const poster = this.poster;
        if (poster) {
          poster.hidden = false;
          if (hadFocus) poster.focus({ preventScroll: true });
        }
      }

      postToFrame(action) {
        const frame = this.player;
        if (!frame || !frame.contentWindow || typeof frame.contentWindow.postMessage !== 'function') return;
        const provider = frame.getAttribute('data-provider');
        try {
          if (provider === 'youtube') {
            const func = action === 'play' ? 'playVideo' : 'pauseVideo';
            frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: func, args: '' }), '*');
          } else if (provider === 'vimeo') {
            frame.contentWindow.postMessage(JSON.stringify({ method: action }), '*');
          }
        } catch (error) {
          // A frame that is not ready yet simply keeps its own state.
        }
      }

      onMediaPlay() {
        this.classList.add('is-playing');
        S.pubsub.publish('video:play', { source: this, preview: this.previewing });
      }

      onMediaPause() {
        this.classList.remove('is-playing');
      }

      onOtherPlay(payload) {
        if (!payload || payload.source === this || !this.player) return;
        if (payload.preview && !this.previewing) return;
        this.pause();
      }

      observe() {
        if (this.observer || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(this.onIntersect, { threshold: [0, 0.25] });
        this.observer.observe(this);
      }

      onIntersect(entries) {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        if (!entry.isIntersecting || entry.intersectionRatio < 0.25) this.pause();
      }
    }
    customElements.define('yarnly-video-embed', YarnlyVideoEmbed);
  }
  S.VideoEmbed = customElements.get('yarnly-video-embed');

  /* ------------------------------------------------------------------------ */
  /* <yarnly-localization-form>                                              */
  /* ------------------------------------------------------------------------ */

  // Disclosure list for {% form 'localization' %}: picking an item writes the
  // hidden input and submits the form. Closes on Escape, outside click, blur.
  if (!customElements.get('yarnly-localization-form')) {
    class YarnlyLocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onListClick = this.onListClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.isOpen = false;
      }

      connectedCallback() {
        this.button = this.querySelector('[data-localization-button]');
        this.list = this.querySelector('[data-localization-list]');
        this.input = this.querySelector('[data-localization-input]');
        if (!this.button || !this.list) return;
        this.button.addEventListener('click', this.onButtonClick);
        this.list.addEventListener('click', this.onListClick);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('focusout', this.onFocusOut);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onButtonClick);
        if (this.list) this.list.removeEventListener('click', this.onListClick);
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('focusout', this.onFocusOut);
        document.removeEventListener('click', this.onDocumentClick);
      }

      get items() {
        return Array.from(this.list.querySelectorAll('[data-localization-item]'));
      }

      onButtonClick(event) {
        event.preventDefault();
        if (this.isOpen) this.close();
        else this.open();
      }

      open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.list.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        document.addEventListener('click', this.onDocumentClick);
        const current = this.list.querySelector('[aria-current]') || this.items[0];
        if (current) current.focus({ preventScroll: true });
      }

      close(returnFocus) {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.list.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        document.removeEventListener('click', this.onDocumentClick);
        if (returnFocus) this.button.focus({ preventScroll: true });
      }

      onListClick(event) {
        const item = event.target.closest('[data-localization-item]');
        if (!item) return;
        event.preventDefault();
        this.select(item);
      }

      select(item) {
        const value = item.getAttribute('data-value');
        const input = this.input;
        if (!input || value === null) return;
        input.value = value;
        this.close();
        const form = input.form || this.querySelector('form');
        if (form) form.submit();
      }

      onKeyDown(event) {
        if (event.key === 'Escape') {
          if (!this.isOpen) return;
          event.preventDefault();
          this.close(true);
          return;
        }
        if (!this.isOpen) return;
        const items = this.items;
        const index = items.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          (items[index + 1] || items[0]).focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          (items[index - 1] || items[items.length - 1]).focus();
        } else if (event.key === 'Home') {
          event.preventDefault();
          items[0] && items[0].focus();
        } else if (event.key === 'End') {
          event.preventDefault();
          items[items.length - 1] && items[items.length - 1].focus();
        }
      }

      onFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.close();
      }

      onDocumentClick(event) {
        if (this.contains(event.target)) return;
        this.close();
      }
    }
    customElements.define('yarnly-localization-form', YarnlyLocalizationForm);
  }
  S.LocalizationForm = customElements.get('yarnly-localization-form');

  /* ------------------------------------------------------------------------ */
  /* <yarnly-product-recommendations>                                        */
  /* ------------------------------------------------------------------------ */

  // Fetches its own markup once it is close to the viewport, and gets out of
  // the way when the shop has nothing to recommend.
  if (!customElements.get('yarnly-product-recommendations')) {
    class YarnlyProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.getAttribute('data-loaded') === 'true') return;
        if (!this.getAttribute('data-url') || typeof window.fetch !== 'function') return;
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.observer.disconnect();
              this.load();
            },
            { rootMargin: '0px 0px 400px 0px' }
          );
          this.observer.observe(this);
        } else {
          this.load();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      load() {
        fetch(this.getAttribute('data-url'))
          .then((response) => {
            if (!response.ok) throw new Error('Recommendations request failed');
            return response.text();
          })
          .then((text) => {
            const doc = S.utils.parseHTML(text);
            const selector = this.getAttribute('data-selector');
            let source = doc.querySelector(selector || 'yarnly-product-recommendations');
            if (!source && !selector) {
              // A static section (e.g. cart-recommendations) carries no element
              // of its own: take the section wrapper's first child instead.
              const section = doc.querySelector('[id^="shopify-section-"]');
              source = section ? section.firstElementChild : null;
            }
            if (!source || !source.innerHTML.trim()) {
              this.hide();
              return;
            }
            this.innerHTML = source.innerHTML;
            this.setAttribute('data-loaded', 'true');
            if (S.utils.observeReveals) S.utils.observeReveals(this);
          })
          .catch(() => this.hide());
      }

      hide() {
        this.hidden = true;
        // Only a whole recommendations section collapses; an in-page block
        // must never take its host section down with it.
        if (this.getAttribute('data-hide-section') !== 'true') return;
        if (window.Shopify && window.Shopify.designMode) return;
        const section = closestFrom(this, '.section');
        if (section) section.hidden = true;
      }
    }
    customElements.define('yarnly-product-recommendations', YarnlyProductRecommendations);
  }
  S.ProductRecommendations = customElements.get('yarnly-product-recommendations');

  /* ------------------------------------------------------------------------ */
  /* Reveal on scroll + editor bridge                                          */
  /* ------------------------------------------------------------------------ */

  // `.reveal` elements get `is-visible` when they enter the viewport. When
  // animations are off (setting or reduced motion) everything is revealed at
  // once. Re-run on `shopify:section:load`; reveal selected sections in the
  // editor immediately so merchants never see an invisible block.
  function revealAll(root) {
    (root || document).querySelectorAll('.reveal:not(.is-visible)').forEach((element) => {
      element.classList.add('is-visible');
    });
  }

  function revealEnabled() {
    return (
      S.settings.animations !== false &&
      S.settings.revealOnScroll !== false &&
      !isReducedMotion() &&
      'IntersectionObserver' in window
    );
  }

  function observeReveals(root) {
    if (!revealEnabled()) {
      revealAll(root);
      return;
    }
    if (!S._revealObserver) {
      S._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            S._revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );
    }
    const scope = root || document;
    scope.querySelectorAll('.reveal:not(.is-visible)').forEach((element) => S._revealObserver.observe(element));
    if (scope !== document && scope.classList && scope.classList.contains('reveal') && !scope.classList.contains('is-visible')) {
      S._revealObserver.observe(scope);
    }
  }
  S.utils.observeReveals = observeReveals;
  S.utils.revealAll = revealAll;

  if (!S._revealBound) {
    S._revealBound = true;

    const start = () => observeReveals(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();

    document.addEventListener('shopify:section:load', (event) => {
      observeReveals(event.target);
    });

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        revealAll(event.target);
      });
      document.addEventListener('shopify:block:select', (event) => {
        revealAll(event.target);
      });
    }
  }
})();
